import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import { toast } from 'sonner';
import { Upload, X, Loader2, ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API_URL } from '@/lib/api';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_RAW_SIZE  = 50 * 1024 * 1024; // 50 MB raw input limit

const COMPRESSION_OPTIONS = {
  maxSizeMB:        1,       // compress down to ≤ 1 MB
  maxWidthOrHeight: 800,     // cap longest side at 800 px
  useWebWorker:     true,    // non-blocking
  fileType:         'image/webp', // always output WebP
};

function formatBytes(bytes) {
  if (bytes < 1024)         return `${bytes} B`;
  if (bytes < 1024 * 1024)  return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export function ImageUpload({
  value,
  onChange,
  folder      = 'images',
  label       = 'Image',
  placeholder = 'Upload or paste URL',
  className   = '',
}) {
  const [uploading,   setUploading]   = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [progress,    setProgress]    = useState('');   // status text
  const [preview,     setPreview]     = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => { setPreview(value || ''); }, [value]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Invalid file type. Use JPEG, PNG, GIF or WebP');
      return;
    }
    if (file.size > MAX_RAW_SIZE) {
      toast.error(`File too large (max ${formatBytes(MAX_RAW_SIZE)})`);
      return;
    }

    try {
      /* ── Step 1: Compress ── */
      setCompressing(true);
      setProgress(`Compressing… (${formatBytes(file.size)})`);

      const compressed = await imageCompression(file, COMPRESSION_OPTIONS);

      setProgress(
        `${formatBytes(file.size)} → ${formatBytes(compressed.size)} · uploading…`
      );
      setCompressing(false);

      /* ── Step 2: Upload ── */
      setUploading(true);
      const formData = new FormData();
      // Give the compressed blob the .webp extension
      formData.append('image', compressed, compressed.name.replace(/\.[^.]+$/, '.webp'));
      formData.append('folder', folder);

      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/upload`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const url = res.data?.url;
      if (url) {
        onChange?.(url);
        setPreview(url);
        toast.success(`Uploaded · ${formatBytes(compressed.size)}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      setCompressing(false);
      setProgress('');
      e.target.value = '';
    }
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    onChange?.(url);
    setPreview(url);
  };

  const handleClear = () => {
    onChange?.('');
    setPreview('');
    setProgress('');
  };

  const busy = uploading || compressing;

  return (
    <div className={`space-y-3 ${className}`}>
      <Label>{label}</Label>

      {/* Preview */}
      {preview && (
        <div className="relative inline-block">
          <img
            src={preview}
            alt="Preview"
            className="h-24 w-24 rounded-lg object-cover border border-slate-200"
            onError={() => setPreview('')}
          />
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
            onClick={handleClear}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      {/* Upload / URL row */}
      <div className="flex gap-2">
        <Input
          ref={inputRef}
          type="file"
          accept={ALLOWED_TYPES.join(',')}
          onChange={handleFileSelect}
          className="hidden"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="shrink-0"
        >
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin mr-1" />
          ) : (
            <Upload className="h-4 w-4 mr-1" />
          )}
          {compressing ? 'Compressing…' : uploading ? 'Uploading…' : 'Upload'}
        </Button>
        <Input
          placeholder={placeholder}
          value={value || ''}
          onChange={handleUrlChange}
          className="flex-1"
          disabled={busy}
        />
      </div>

      {/* Compression status */}
      {progress ? (
        <p className="text-xs text-blue-600 flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          {progress}
        </p>
      ) : (
        <p className="text-xs text-slate-500 flex items-center gap-1">
          <ImageIcon className="h-3 w-3" />
          Auto-compressed to WebP · max 1 MB · 800 px — JPEG, PNG, GIF or WebP accepted
        </p>
      )}
    </div>
  );
}
